<template>
  <v-app>
    <!-- Splash screen while checking auth -->
    <SplashScreen v-if="isCheckingAuth" :message="splashMessage" />

    <!-- Main app content -->
    <template v-else>
      <router-view v-slot="{ Component, route }">
        <AuthenticatedLayout v-if="route.meta.layout === 'authenticated'">
          <component :is="Component" :key="route.meta.remountOnPathChange ? route.path : route.params.tryoutId ? `${route.params.clubId}/${route.params.seasonId}/${route.params.tryoutId}` : undefined" />
        </AuthenticatedLayout>
        <component :is="Component" v-else />
      </router-view>
    </template>

    <BuildInfoBadge v-show="!matchDisplay.fullscreen" />

    <!-- Offline indicator -->
    <v-snackbar
      v-if="!isOnline"
      location="bottom"
      persistent
      color="warning"
      class="offline-snackbar"
    >
      <v-icon start>mdi-wifi-off</v-icon>
      {{ t('app.offline') }}
    </v-snackbar>

    <!-- Sync status indicator -->
    <v-snackbar
      v-if="isSyncing"
      location="top"
      persistent
      color="info"
      class="sync-snackbar"
    >
      <v-icon start>mdi-sync</v-icon>
      {{ t('app.syncing', { count: queueLength }) }}
    </v-snackbar>
  </v-app>
</template>

<script setup lang="ts">
  import { useMatchDisplayStore } from '@/stores/matchDisplay'
  const matchDisplay = useMatchDisplayStore()
  import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRoute, useRouter } from 'vue-router'
  import { useBetaStore } from '@/stores/beta'
  import { usePhotoFramingStore } from '@/stores/photoFraming'
  import { useAuthStore } from '@/stores/auth'
  import { useOfflineStore } from '@/stores/offline'
  import { useAuth } from '@/auth/composable'
  import BuildInfoBadge from '@/components/BuildInfoBadge.vue'
  import SplashScreen from '@/components/SplashScreen.vue'
  import AuthenticatedLayout from '@/layouts/AuthenticatedLayout.vue'
  import { appSocket } from '@/services/socket'

  import { usePreparedSessions } from '@/features/offline-capture/preparedSessions'
  const preparedSessions = usePreparedSessions()
  onMounted(preparedSessions.start)
  onUnmounted(preparedSessions.stop)

  const photoFraming = usePhotoFramingStore()
  onMounted(photoFraming.start)
  onUnmounted(photoFraming.stop)
  const route = useRoute(), router = useRouter(), beta = useBetaStore()
  watch(() => beta.enabled, enabled => {
    if (enabled) return
    if (route.name === 'offline-capture') void router.replace({path:`/clubhouse/${route.params.clubId}/season/${route.params.seasonId}/tryout/${route.params.tryoutId}/capture`,query:route.query})
    else if (route.name === 'offline-capture-resume') void router.replace('/')
  })
  const authStore = useAuthStore()
  const offlineStore = useOfflineStore()
  watch(() => [authStore.token, authStore.user?.id], () => { void photoFraming.sync() })
  watch(() => [authStore.token, authStore.user?.id, authStore.isMasquerading], preparedSessions.identityChanged, { flush: 'sync' })
  const { getCurrentUser } = useAuth()
  const { t } = useI18n()

  const isCheckingAuth = ref(true)
  const splashMessage = ref(t('app.session.restoring'))
  const isOnline = computed(() => offlineStore.isOnline)
  const isSyncing = computed(() => offlineStore.isSyncing)
  const queueLength = computed(() => offlineStore.queueLength)

  const stopSocketAuthWatch = watch(() => authStore.token, (token) => {
    if (token) appSocket.connect(token)
    else appSocket.disconnect()
  }, { immediate: true })

  // Check authentication and restore session on app mount
  onMounted(async () => {
    try {
      // User state is restored from localStorage. Only fetch the profile if
      // there is a token but no stored user to hydrate the auth store.
      if (authStore.token && !authStore.user && route.name !== 'offline-capture-resume' && navigator.onLine) {
        splashMessage.value = t('app.session.loadingProfile')
        const user = await getCurrentUser()
        if (!user) {
          authStore.logout()
        }
      }
    } catch (error) {
      console.error('Session restoration failed:', error)
      authStore.logout()
    } finally {
      isCheckingAuth.value = false
    }
  })

  onUnmounted(() => {
    stopSocketAuthWatch()
    appSocket.disconnect()
  })
</script>

<style scoped>
  :deep(.offline-snackbar) {
    bottom: 80px !important;
  }

  :deep(.sync-snackbar) {
    top: 60px !important;
  }
</style>

<style>
  .app-long-press-drag-active,
  .app-long-press-drag-active * {
    user-select: none !important;
    -webkit-user-select: none !important;
    -webkit-touch-callout: none !important;
  }

  .app-long-press-drag-ghost {
    box-shadow: 0 14px 28px rgba(15, 23, 42, 0.22) !important;
    opacity: 0.88;
    pointer-events: none !important;
    position: fixed !important;
    left: 0;
    top: 0;
    transform-origin: top left;
    z-index: 10000;
  }
</style>
